import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  InboxOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Alert, Button, Divider, Flex, Modal, Progress, Upload } from 'antd';
import type { RcFile, UploadProps } from 'antd/es/upload/interface';

import BookPreviewModal from 'components/Book/BookPreviewModal';
import Box from 'components/Box/Box';

import useLayoutStore from 'hooks/stores/useLayoutStore';
import useProductsStore from 'hooks/stores/useProductsStore';
import useSessionStore from 'hooks/stores/useSessionStore';

import API from 'services/API';
import { downloadFileToClient } from 'services/downloadService';
import { uploadAssetFile } from 'services/uploadService';

export type AssetsActionsVariant = 'sidebar' | 'toolbar';

type Props = {
  showAddBlankPage: boolean;
  // 'sidebar' (default): gestapelde, opvallende knoppen voor de smalle SidesList-kolom.
  // 'toolbar': één compacte rij (upload links, kleine neutrale knoppen rechts) voor de
  // booklayout-view, waar dit blok over de volle breedte boven het paginagrid staat.
  variant?: AssetsActionsVariant;
};

// Compacte styling voor de upload-dragger, gedeeld door beide varianten.
const UPLOAD_DRAGGER_STYLES = `
  .custom-upload-container .ant-upload-drag {
    height: 45px !important;
    padding: 2px !important;
    display: flex;
    align-items: center;
    overflow: hidden;
  }
  .custom-upload-container .ant-upload-drag-container {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px !important;
  }
  .custom-upload-container .ant-upload-drag-icon {
    margin: 0 4px 0 0 !important;
  }
  .custom-upload-container .ant-upload-text {
    margin: 0 !important;
    font-size: 12px !important;
  }
  .custom-upload-container .anticon-inbox {
    font-size: 16px !important;
  }
`;

const AssetsAndSidesActions: React.FC<Props> = ({ showAddBlankPage, variant = 'sidebar' }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const sessionId = useSessionStore((state) => state.sessionId);
  const session = useSessionStore((state) => state.session);
  const setVisible = useLayoutStore((state) => state.setShouldDisplayAssetsProcessing);
  const { products, selectedProductId, getSelectedProduct } = useProductsStore((state) => ({
    products: state.products,
    selectedProductId: state.selectedProductId,
    getSelectedProduct: state.getSelectedProduct,
  }));

  // Get UI sideList configuration from session options (default = visible)
  const uiSideList = session?.options?.ui?.sideList?.buttons;
  const shouldShowAddBlankPage = uiSideList?.addBlankPage !== false && showAddBlankPage;
  const shouldShowDeleteAll = uiSideList?.deleteAll !== false && showAddBlankPage;
  const shouldShowDownloadPreview = uiSideList?.downloadPreview !== false;
  const shouldShowBookPreview = uiSideList?.bookPreview !== false;

  // Get UI upload configuration from session options (default = enabled)
  const selectedProduct = getSelectedProduct();
  const isSidesFull = (selectedProduct?.sides?.length ?? 0) >= (session?.sidesCount ?? 0);
  const shouldShowUpload = session?.options?.ui?.enableUpload !== false && !isSidesFull;

  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isBookPreviewOpen, setIsBookPreviewOpen] = useState(false);
  const [bookPreviewProductId, setBookPreviewProductId] = useState<string | null>(null);

  const { mutate: deleteAllSidesMutation } = useMutation(
    'deleteSide',
    () => {
      const selectedProduct = getSelectedProduct();
      const productId = selectedProduct?.id;

      console.log('[DEBUG] Delete all sides - Selected product:', selectedProduct);
      console.log('[DEBUG] Delete all sides - ProductId:', productId);

      if (!productId) {
        throw new Error('No product selected for deletion');
      }

      return API.deleteAllSidesForProduct(sessionId, productId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('products');
      },
    },
  );
  const onChange: UploadProps<File>['onChange'] = async ({ file: { originFileObj } }) => {
    try {
      setIsUploading(true);
      await uploadAssetFile({
        sessionId,
        file: originFileObj as RcFile,
        productId: selectedProductId || undefined,
        onProgress: setUploadProgress,
      });
    } catch (error) {
      setError('Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddBlankPage = async () => {
    try {
      setIsLoading(true);
      const selectedProduct = getSelectedProduct();
      const productId = selectedProduct?.id;

      const response = await API.postCreateBlankPage(sessionId, productId);
      if (response.status !== 202) throw new Error('Generate blank page failed');
      setVisible(true);
    } catch (error) {
      setError('Upload failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPreview = async () => {
    try {
      if (!products || products.length === 0) {
        setError('No products found to download preview');
        return;
      }

      // Use the selected product from centralized state
      const selectedProduct = getSelectedProduct();
      const productId = selectedProduct?.id || products[0]?.id;

      if (!productId) {
        setError('No product selected for download');
        return;
      }

      setIsDownloading(true);
      setDownloadProgress(0);

      // Vraag de gecombineerde PDF aan
      const { fileUrl, fileName } = await API.combinePdfPreviews(sessionId, productId);

      // Download het bestand naar de client
      await downloadFileToClient(fileUrl, fileName, (progress) => {
        setDownloadProgress(progress.value);
      });

      setError(null);
    } catch (error) {
      console.error('Download preview error:', error);
      setError('Failed to download preview');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleOpenBookPreview = () => {
    if (!products || products.length === 0) {
      setError('No products found to preview');
      return;
    }

    const selectedProduct = getSelectedProduct();
    const productId = selectedProduct?.id || products[0]?.id;

    if (!productId) {
      setError('No product selected for preview');
      return;
    }

    // The book viewer renders straight from each side's own previewImageUrl, so it can
    // open instantly — no combinePdfPreviews round trip needed just to view the book.
    setBookPreviewProductId(productId);
    setIsBookPreviewOpen(true);
    setError(null);
  };

  const confirmDeleteAllModal = (
    <Modal
      title="Confirm Delete"
      open={isModalOpen}
      onCancel={() => setIsModalOpen(false)}
      footer={null}
    >
      <p>{t('DeletePageConfirmation')}</p>
      <Flex justify="flex-end">
        <Button onClick={() => setIsModalOpen(false)}>{t('Cancel')}</Button>
        <Button
          style={{ marginLeft: '1rem' }}
          onClick={() => {
            setIsModalOpen(false);
            deleteAllSidesMutation();
          }}
          danger
        >
          {t('Delete')}
        </Button>
      </Flex>
    </Modal>
  );

  const bookPreviewModal = isBookPreviewOpen && bookPreviewProductId && (
    <BookPreviewModal
      isOpen={isBookPreviewOpen}
      onClose={() => setIsBookPreviewOpen(false)}
      sessionId={sessionId}
      productId={bookPreviewProductId}
    />
  );

  if (variant === 'toolbar') {
    const hasProducts = !!products && products.length > 0;
    return (
      <Box>
        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 8 }}
          />
        )}
        <Flex align="center" justify="space-between" gap={12} wrap="wrap">
          <div style={{ flex: '0 1 340px', minWidth: 220 }}>
            {shouldShowUpload && !isUploading && (
              <div className="custom-upload-container">
                <style>{UPLOAD_DRAGGER_STYLES}</style>
                <Upload.Dragger onChange={onChange} multiple={true} accept=".pdf,.jpg,.jpeg,.png">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <InboxOutlined style={{ fontSize: '16px', marginRight: '4px' }} />
                    <span style={{ fontSize: '12px' }}>{t('Drag file or click to upload')}</span>
                  </div>
                </Upload.Dragger>
              </div>
            )}
            {isUploading && (
              <Flex align="center" gap={8}>
                <Progress percent={uploadProgress} status="active" size="small" />
                <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{t('Uploading')}</span>
              </Flex>
            )}
          </div>
          <Flex align="center" gap={6} wrap="wrap">
            {shouldShowAddBlankPage && (
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={handleAddBlankPage}
                loading={isLoading}
              >
                {t('Add Blank Page')}
              </Button>
            )}
            {shouldShowDownloadPreview && (
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={handleDownloadPreview}
                loading={isDownloading}
                disabled={!hasProducts}
              >
                {t('Download preview')}
              </Button>
            )}
            {shouldShowBookPreview && (
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={handleOpenBookPreview}
                disabled={!hasProducts}
              >
                {t('BookPreview.ViewPreview')}
              </Button>
            )}
            {shouldShowDeleteAll && (
              <Button
                size="small"
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => setIsModalOpen(true)}
              >
                {t('DeleteAll')}
              </Button>
            )}
          </Flex>
        </Flex>
        {isDownloading && (
          <Progress
            percent={downloadProgress}
            status={downloadProgress === 100 ? 'success' : 'active'}
            size="small"
            style={{ marginTop: 8 }}
          />
        )}
        {confirmDeleteAllModal}
        {bookPreviewModal}
      </Box>
    );
  }

  return (
    <Box mt={1} style={{ marginTop: '4px' }}>
      {error && (
        <Alert message={error} type="error" showIcon closable onClose={() => setError(null)} />
      )}

      {shouldShowUpload && !isUploading && (
        <div className="custom-upload-container" style={{ position: 'relative' }}>
          <style>{UPLOAD_DRAGGER_STYLES}</style>
          <Upload.Dragger onChange={onChange} multiple={true} accept=".pdf,.jpg,.jpeg,.png">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <InboxOutlined style={{ fontSize: '16px', marginRight: '4px' }} />
              <span style={{ fontSize: '12px' }}>
                <b>{t('Drag file or click to upload')}</b>
              </span>
            </div>
          </Upload.Dragger>
        </div>
      )}

      {isUploading && (
        <>
          <Progress percent={uploadProgress} status="active" />
          <h5 style={{ textAlign: 'center' }}>{t('Uploading')}</h5>
        </>
      )}

      <Divider style={{ margin: '6px 0' }} />

      {(shouldShowAddBlankPage || shouldShowDeleteAll) && (
        <>
          {confirmDeleteAllModal}
          <Flex wrap="wrap" gap={3} className="button-container" style={{ marginBottom: '6px' }}>
            {shouldShowAddBlankPage && (
              <Button
                type="primary"
                onClick={handleAddBlankPage}
                loading={isLoading}
                icon={<PlusOutlined />}
                style={{
                  flex: shouldShowDeleteAll ? '1 0 calc(50% - 3px)' : '1 0 100%',
                  minWidth: '100px',
                  backgroundColor: '#52c41a',
                  borderColor: '#52c41a',
                  height: '26px',
                  padding: '2px 6px',
                  fontSize: '12px',
                }}
              >
                {t('Add Blank Page')}
              </Button>
            )}
            {shouldShowDeleteAll && (
              <Button
                type="primary"
                danger
                onClick={() => setIsModalOpen(true)}
                style={{
                  flex: shouldShowAddBlankPage ? '1 0 calc(50% - 3px)' : '1 0 100%',
                  minWidth: '100px',
                  height: '26px',
                  padding: '2px 6px',
                  fontSize: '12px',
                }}
              >
                {t('DeleteAll')}
              </Button>
            )}
          </Flex>
        </>
      )}

      {/* Download Preview & Book Preview Buttons */}
      {(shouldShowDownloadPreview || shouldShowBookPreview) && (
        <Flex gap={3} style={{ marginBottom: '6px' }}>
          {shouldShowDownloadPreview && (
            <Button
              type="primary"
              onClick={handleDownloadPreview}
              loading={isDownloading}
              icon={<DownloadOutlined />}
              disabled={!products || products.length === 0}
              style={{
                flex: shouldShowBookPreview ? '1 0 calc(50% - 3px)' : '1 0 100%',
                minWidth: '100px',
                backgroundColor: '#1890ff',
                borderColor: '#1890ff',
                height: '26px',
                padding: '2px 6px',
                fontSize: '12px',
              }}
            >
              {t('Download preview')}
            </Button>
          )}
          {shouldShowBookPreview && (
            <Button
              type="primary"
              onClick={handleOpenBookPreview}
              icon={<EyeOutlined />}
              disabled={!products || products.length === 0}
              style={{
                flex: shouldShowDownloadPreview ? '1 0 calc(50% - 3px)' : '1 0 100%',
                minWidth: '100px',
                backgroundColor: '#722ed1',
                borderColor: '#722ed1',
                height: '26px',
                padding: '2px 6px',
                fontSize: '12px',
              }}
            >
              {t('BookPreview.ViewPreview')}
            </Button>
          )}
        </Flex>
      )}

      {isDownloading && (
        <Progress
          percent={downloadProgress}
          status={downloadProgress === 100 ? 'success' : 'active'}
          size="small"
          style={{ marginBottom: '12px' }}
        />
      )}

      {bookPreviewModal}
    </Box>
  );
};

export default AssetsAndSidesActions;
